import { useEffect, useState } from "react";
import { Broadcast, isBroadcastLive, listBroadcasts } from "../api/broadcast.api";

/**
 * Shows the currently-active broadcasts at the top of the layout.
 * Users can dismiss each one for their session (persisted in sessionStorage by id).
 */
const BroadcastBanner: React.FC = () => {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(() => {
    try {
      const raw = sessionStorage.getItem("dismissed_broadcasts");
      return new Set<number>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<number>();
    }
  });

  // Refresh every 60s so newly-published broadcasts appear without a page reload.
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const rows = await listBroadcasts();
        if (!cancelled) setBroadcasts(rows);
      } catch {
        /* ignore — banner is non-critical */
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const dismiss = (id: number) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try {
      sessionStorage.setItem("dismissed_broadcasts", JSON.stringify(Array.from(next)));
    } catch { /* ignore */ }
  };

  const now = new Date();
  const visible = broadcasts.filter((b) => isBroadcastLive(b, now) && !dismissed.has(b.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-1.5 px-3 pt-3">
      {visible.map((b) => (
        <div
          key={b.id}
          className="rounded-2xl px-4 py-3 flex items-start gap-3"
          style={{
            background: "linear-gradient(135deg,#FFFBEB 0%,#FEE2C7 100%)",
            boxShadow: "0 0 0 1px #FDBA74",
          }}
          role="status"
        >
          <span className="text-lg leading-none flex-shrink-0 mt-0.5">📣</span>
          <p
            className="flex-1 min-w-0 text-sm font-semibold whitespace-pre-wrap break-words"
            style={{ color: "#5B3A29" }}
          >
            {b.text}
          </p>
          <button
            type="button"
            onClick={() => dismiss(b.id)}
            aria-label="Dismiss"
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-amber-200/60"
            style={{ color: "#5B3A29" }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

export default BroadcastBanner;
